/**
 * API Service
 * Handles all HTTP requests to the backend server
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface LoginResponse {
  success: boolean;
  message: string;
  user: {
    id: string;
    email: string;
    name: string;
    avatar?: string;
    username?: string;
    accountType?: string;
    accountRole?: string;
    corporateAccountId?: string;
    hasProfile: boolean;
    isCorporate: boolean;
    companyName?: string;
    canAccessTeamManagement: boolean;
    hasTeams: boolean;
    teams?: any[];
    subscription?: any;
    aiNotesCredits?: any;
    authMethod?: string;
    hasPassword?: boolean;
    isFirstLogin?: boolean;
  };
  token: string;
  domain?: string;
  authMethods?: string[];
  navConfig?: any;
  debug?: any;
}

interface SignupResponse {
  success: boolean;
  message: string;
  profile?: {
    _id: string;
    email: string;
    name: string;
    username: string;
  };
  token?: string;
  redirectToVerification?: boolean;
  verificationToken?: string;  // Token for verification URL
  accountType?: string;
}

class ApiService {
  private baseUrl: string;
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];
  
  // Request deduplication - prevent duplicate concurrent requests
  private pendingRequests: Map<string, Promise<any>> = new Map();

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private onTokenRefreshed(token: string) {
    this.refreshSubscribers.forEach((callback) => callback(token));
    this.refreshSubscribers = [];
  }

  private addRefreshSubscriber(callback: (token: string) => void) {
    this.refreshSubscribers.push(callback);
  }
  
  /**
   * Generate a unique key for request deduplication
   */
  private getRequestKey(endpoint: string, options: RequestInit): string {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}:${endpoint}:${body}`;
  }

  /**
   * Generic request handler with automatic token refresh and request deduplication
   */
  public async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    // Request deduplication for GET requests to prevent duplicate calls
    const requestKey = this.getRequestKey(endpoint, options);
    const method = options.method || 'GET';
    
    if (method === 'GET' && this.pendingRequests.has(requestKey)) {
      console.log(`🔄 Deduplicating request: ${endpoint}`);
      return this.pendingRequests.get(requestKey)!;
    }
    
    const url = `${this.baseUrl}${endpoint}`;

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add auth token if available (impersonation token takes priority)
    const token = sessionStorage.getItem('impersonation_token') || localStorage.getItem('auth_token');
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      credentials: 'include', // Include cookies for session management
    };

    // FormData bodies must let the browser set its own multipart boundary header —
    // a stale 'Content-Type: application/json' here makes the server try to JSON.parse
    // the raw multipart body and fail with "Unexpected token '-'" on the boundary text.
    if (config.body instanceof FormData) {
      delete (config.headers as Record<string, string>)['Content-Type'];
    }

    const requestPromise = (async () => {
      try {
        const response = await fetch(url, config);
      
      // Parse response data first to check error message
      const data = await response.json().catch(() => ({}));
      
      // Extract cache version headers if present
      const cacheVersion = response.headers.get('X-Profile-Cache-Version');
      const lastModified = response.headers.get('X-Profile-Last-Modified');
      
      // Add cache metadata to response if headers present
      if (cacheVersion || lastModified) {
        data.cacheVersion = cacheVersion;
        data.lastModified = lastModified;
      }
      
      // Handle 401 Unauthorized
      if (response.status === 401) {
        const isAuthEndpoint = endpoint.includes('/api/auth/login') || 
                             endpoint.includes('/api/auth/refresh') ||
                             endpoint.includes('/api/auth/google');
        
        const errorMessage = data.message || data.error || '';
        
        // Deactivated / deleted account — skip refresh, logout immediately
        if (
          errorMessage.includes('deactivated') ||
          errorMessage.includes('no longer exists') ||
          errorMessage.includes('Invalid token') ||
          errorMessage.includes('User not found')
        ) {
          console.warn('🚫 Account blocked or invalid token, forcing logout...');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_data');
          localStorage.removeItem('auth_version');
          sessionStorage.removeItem('user_profile');
          sessionStorage.removeItem('profile_cache_version');
          const reason = (errorMessage.includes('deactivated') || errorMessage.includes('no longer exists'))
            ? 'deactivated'
            : undefined;
          window.dispatchEvent(new CustomEvent('auth:token-expired', { detail: { reason } }));
          throw new Error(errorMessage || 'Invalid session. Please login again.');
        }
        
        // Skip token refresh for auth endpoints or if this is a retry
        if (isAuthEndpoint || isRetry) {
          throw new Error(errorMessage || 'Authentication failed');
        }
        
        // Check if token exists
        if (!localStorage.getItem('auth_token')) {
          throw new Error('No authentication token found');
        }

        // If already refreshing, wait for it
        if (this.isRefreshing) {
          return new Promise((resolve, reject) => {
            this.addRefreshSubscriber((newToken: string) => {
              // Retry original request with new token
              this.request<T>(endpoint, options, true)
                .then(resolve)
                .catch(reject);
            });
          });
        }

        // Start refresh process
        this.isRefreshing = true;

        try {
          const refreshResponse = await this.refreshToken();
          
          if (refreshResponse.success && refreshResponse.token) {
            // Update stored token
            localStorage.setItem('auth_token', refreshResponse.token);
            if (refreshResponse.user) {
              localStorage.setItem('user_data', JSON.stringify(refreshResponse.user));
            }

            // Notify all waiting requests
            this.onTokenRefreshed(refreshResponse.token);
            this.isRefreshing = false;

            // Retry original request with new token
            return this.request<T>(endpoint, options, true);
          } else {
            throw new Error('Token refresh failed');
          }
        } catch (refreshError: any) {
          this.isRefreshing = false;

          // Distinguish server-confirmed dead session vs. transient network/5xx failure.
          // Transient failures shouldn't force logout — the original request just fails,
          // caller decides how to handle. Only permanent auth errors clear state.
          const errMessage = String(refreshError?.message || '');
          const isDefinitiveFailure =
            errMessage.includes('deactivated') ||
            errMessage.includes('no longer exists') ||
            errMessage.includes('Invalid token') ||
            errMessage.includes('User not found') ||
            errMessage.includes('Invalid token payload');

          if (isDefinitiveFailure) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_data');
            sessionStorage.removeItem('user_profile');
            window.dispatchEvent(new CustomEvent('auth:token-expired'));
            throw new Error('Session expired. Please login again.');
          }

          // Transient — surface the original error to the caller. Auth state stays intact
          // so the next request can retry with the existing token; a real dead session
          // will be caught by the next request's own 401 handling.
          throw new Error(refreshError?.message || 'Request failed');
        }
      }

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Request failed');
      }

      return data;
    } catch (error: any) {
      // Avoid logging expected 'not found' noise; response is out of try-scope so don't reference it here
      if (!error?.message?.includes('not found')) {
        console.error('API request failed:', error);
      }
      throw error;
    } finally {
      // Cleanup pending request from map
      if (method === 'GET') {
        this.pendingRequests.delete(requestKey);
      }
    }
    })();
    
    // Store promise for deduplication (only for GET requests)
    if (method === 'GET') {
      this.pendingRequests.set(requestKey, requestPromise);
    }
    
    return requestPromise;
  }

  /**
   * Auth endpoints
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async refreshToken(): Promise<LoginResponse> {
    return this.request<LoginResponse>('/api/auth/refresh', {
      method: 'POST',
    });
  }

  async signup(email: string, password: string, name: string, username: string, accountType: string = 'individual'): Promise<SignupResponse> {
    return this.request<SignupResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ 
        email, 
        password, 
        name, 
        username,
        signupData: {
          accountType: accountType
        }
      }),
    });
  }

  async logout(): Promise<{ success: boolean }> {
    return this.request('/api/auth/logout', {
      method: 'POST',
    });
  }

  async getCurrentUser(): Promise<any> {
    return this.request('/api/auth/me', {
      method: 'GET',
    });
  }

  async getUserProfile(): Promise<any> {
    return this.request('/api/auth/user-profile', {
      method: 'GET',
    });
  }

  async verifyEmail(token: string): Promise<any> {
    return this.request('/api/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async forgotPassword(email: string): Promise<any> {
    return this.request('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<any> {
    return this.request('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  /**
   * Google OAuth
   */
  async googleInit(accountType: string = 'individual', companyData?: any): Promise<any> {
    return this.request('/api/auth/google/init', {
      method: 'POST',
      body: JSON.stringify({
        accountType,
        ...companyData
      }),
    });
  }

  getGoogleAuthUrl(): string {
    return `${this.baseUrl}/api/auth/google`;
  }

  /**
   * Profile endpoints
   */
  async getProfile(userId: string): Promise<any> {
    return this.request(`/api/profiles/${userId}`, {
      method: 'GET',
    });
  }

  async updateProfile(userId: string, data: any): Promise<any> {
    return this.request(`/api/profiles/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async saveProfile(data: any): Promise<any> {
    return this.request('/api/profile', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * QR Designer endpoints
   */
  async getQRCodesList(identifier: string): Promise<any> {
    // cache-bust so newly created QR codes appear without a page refresh
    return this.request(`/api/qr-designer/list/${identifier}?_t=${Date.now()}`, {
      method: 'GET',
    });
  }

  async getCustomQRAnalytics(): Promise<any> {
    return this.request('/api/profile/custom-qr-analytics', {
      method: 'GET',
    });
  }

  async getQRAnalytics(qrCodeId: string, days: number = 30): Promise<any> {
    return this.request(`/api/qrana/analytics/history?qrCodeId=${qrCodeId}`, {
      method: 'GET',
    });
  }

  /**
   * Download QR code with custom format - returns blob
   */
  async downloadCustomQRWithFormat(qrCodeId: string, size: string, formats: string[]): Promise<Blob> {
    const url = `${this.baseUrl}/api/qr-designer/downloadcustomqrwithformat`;

    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ qrCodeId, size, formats }),
    });

    if (!response.ok) {
      throw new Error('Failed to download QR code');
    }

    return response.blob();
  }

  /**
   * Delete QR code
   */
  async deleteQRCode(qrCodeId: string): Promise<any> {
    return this.request(`/api/qr-designer/${qrCodeId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Update QR code
   */
  async updateQRCode(qrCodeId: string, data: { name?: string; url?: string }): Promise<any> {
    return this.request(`/api/qr-designer/${qrCodeId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get wallet cards
   */
  async getWalletCards(): Promise<any> {
    return this.request('/api/wallet', {
      method: 'GET',
    });
  }

  /**
   * Remove card from wallet
   */
  async removeCardFromWallet(cardId: string): Promise<any> {
    return this.request(`/api/wallet/remove/${cardId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<any> {
    return this.request('/health', {
      method: 'GET',
    });
  }

  /**
   * Generic HTTP methods for RESTful API calls
   */
  async get<T = any>(endpoint: string, options?: { params?: Record<string, any> }): Promise<{ data: T }> {
    let url = endpoint;
    if (options?.params) {
      const queryString = new URLSearchParams(options.params).toString();
      url = `${endpoint}?${queryString}`;
    }
    const data = await this.request<T>(url, { method: 'GET' });
    return { data };
  }

  async post<T = any>(endpoint: string, body?: any): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  }

  async put<T = any>(endpoint: string, body?: any): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
    return { data };
  }

  async delete<T = any>(endpoint: string): Promise<{ data: T }> {
    const data = await this.request<T>(endpoint, {
      method: 'DELETE',
    });
    return { data };
  }

  // Team Management API Methods
  async getTeams(params?: { page?: number; limit?: number }): Promise<{ data: any }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const query = queryParams.toString() ? `?${queryParams}` : '';
    const data = await this.request(`/api/teams${query}`);
    return { data };
  }

  async createTeam(teamData: { name: string; description?: string }): Promise<{ data: any }> {
    const data = await this.request('/api/teams', {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
    return { data };
  }

  async getTeam(teamId: string): Promise<{ data: any }> {
    const data = await this.request(`/api/teams/${teamId}`);
    return { data };
  }

  async updateTeam(teamId: string, teamData: { name?: string; description?: string }): Promise<{ data: any }> {
    const data = await this.request(`/api/teams/${teamId}`, {
      method: 'PUT',
      body: JSON.stringify(teamData),
    });
    return { data };
  }

  async deleteTeam(teamId: string): Promise<{ data: any }> {
    const data = await this.request(`/api/teams/${teamId}`, {
      method: 'DELETE',
    });
    return { data };
  }

  async inviteTeamMember(
    teamId: string, 
    memberData: { name: string; email: string; role: string }
  ): Promise<{ 
    data: {
      success: boolean;
      message: string;
      roleFromExistingTeam?: boolean;
      requestedRole?: string;
      assignedRole?: string;
      roleMessage?: string;
      member?: any;
      invitationSent?: boolean;
      userType?: string;
      requiresVerification?: boolean;
    }
  }> {
    const data = await this.request(`/api/teams/${teamId}/members`, {
      method: 'POST',
      body: JSON.stringify(memberData),
    });
    return { data: data as unknown } as any;
  }

  async searchInvitableUsers(teamId: string, query: string): Promise<{
    data: { success: boolean; users: Array<{ _id: string; name: string; email: string; profilePhoto?: string }> }
  }> {
    const data = await this.request(
      `/api/teams/${encodeURIComponent(teamId)}/search-members?q=${encodeURIComponent(query)}`
    );
    return { data: data as any };
  }

  async updateTeamMember(teamId: string, memberId: string, memberData: { role?: string; status?: string }): Promise<{ data: any }> {
    const data = await this.request(`/api/teams/${teamId}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify(memberData),
    });
    return { data };
  }

  async resendTeamInvite(teamId: string, memberId: string): Promise<{ data: any }> {
    const data = await this.request(`/api/teams/${teamId}/members/${memberId}/resend-invite`, {
      method: 'POST',
    });
    return { data };
  }

  async removeTeamMember(teamId: string, memberId: string): Promise<{ data: any }> {
    const data = await this.request(`/api/teams/${teamId}/members/${memberId}`, {
      method: 'DELETE',
    });
    return { data };
  }

  async bulkImportTeamMembers(teamId: string, csvFile: File): Promise<{ data: any }> {
    const formData = new FormData();
    formData.append('csvFile', csvFile);
    
    const data = await this.request(`/api/teams/${teamId}/members/bulk-import`, {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header for FormData, let browser set it
      headers: undefined,
    });
    return { data };
  }

  async getTeamAnalytics(teamId: string): Promise<{ data: any }> {
    const data = await this.request(`/api/teams/${teamId}/analytics`);
    return { data };
  }

  async getTeamActivity(teamId: string): Promise<{ data: any }> {
    const data = await this.request(`/api/teams/${teamId}/activity`);
    return { data };
  }

  /**
   * Join a team using team code (public endpoint - no auth required)
   * Handles both new user registration and existing user team joining
   */
  async joinTeamByCode(joinData: {
    teamCode: string;
    email: string;
    name?: string;
    password?: string;
    role?: string;
  }): Promise<{
    success: boolean;
    message: string;
    token?: string;
    user?: any;
    team?: any;
    isNewUser?: boolean;
    roleFromExistingTeam?: boolean;
    requestedRole?: string;
    assignedRole?: string;
    roleMessage?: string;
  }> {
    const data = await this.request('/api/teams/join-by-code', {
      method: 'POST',
      body: JSON.stringify(joinData),
      // This is a public endpoint, so we don't include auth headers
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return (data as unknown) as { success: boolean; message: string; token?: string; user?: any; team?: any; isNewUser?: boolean; roleFromExistingTeam?: boolean; requestedRole?: string; assignedRole?: string; roleMessage?: string; };
  }

  /**
   * Check if email exists in the system (for team join flow)
   */
  async checkEmailExists(email: string): Promise<{ exists: boolean; hasPassword: boolean }> {
    try {
      const data = await this.request(`/api/auth/check-email?email=${encodeURIComponent(email)}`);
      return (data as unknown) as { exists: boolean; hasPassword: boolean };
    } catch (error) {
      return { exists: false, hasPassword: false };
    }
  }

  // ============== SUBSCRIPTION MANAGEMENT ==============

  /**
   * Create Stripe checkout session for plan upgrade
   */
  async createCheckoutSession(planId: string, billingCycle: 'monthly' | 'annual' = 'monthly'): Promise<{
    success: boolean;
    sessionId: string;
    checkoutUrl: string;
  }> {
    return this.request('/api/subscription/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify({ planId, billingCycle }),
    });
  }

  /**
   * Create Stripe checkout session for AI Notes credit purchase
   */
  async createCreditCheckout(minutes: number, price: number): Promise<{
    success: boolean;
    sessionId: string;
    checkoutUrl: string;
  }> {
    return this.request('/api/subscription/create-credit-checkout', {
      method: 'POST',
      body: JSON.stringify({ minutes, price }),
    });
  }

  /**
   * Get current subscription status
   */
  async getSubscriptionStatus(): Promise<{
    success: boolean;
    subscription: {
      plan: string;
      status: string;
      currentPeriodStart?: Date;
      currentPeriodEnd?: Date;
      cancelAtPeriodEnd?: boolean;
      billingCycle?: string;
    };
  }> {
    return this.request('/api/subscription/status');
  }

  /**
   * Cancel subscription
   */
  async cancelSubscription(immediate: boolean = false): Promise<{
    success: boolean;
    message: string;
    subscription: any;
  }> {
    return this.request('/api/subscription/cancel', {
      method: 'POST',
      body: JSON.stringify({ immediate }),
    });
  }

  /**
   * Reactivate cancelled subscription
   */
  async reactivateSubscription(): Promise<{
    success: boolean;
    message: string;
    subscription: any;
  }> {
    return this.request('/api/subscription/reactivate', {
      method: 'POST',
    });
  }

  /**
   * Change billing cycle (monthly <-> annual)
   */
  async changeBillingCycle(billingCycle: 'monthly' | 'annual'): Promise<{
    success: boolean;
    message: string;
    subscription: any;
  }> {
    return this.request('/api/subscription/change-billing', {
      method: 'POST',
      body: JSON.stringify({ billingCycle }),
    });
  }

  /**
   * Get billing / payment history
   */
  async getBillingHistory(): Promise<{
    success: boolean;
    transactions: Array<{
      id: string;
      type: string;
      description: string;
      amount: number;
      currency: string;
      status: string;
      createdAt: number;
    }>;
  }> {
    return this.request('/api/subscription/billing-history');
  }

  // ── Issued Cards (corporate marketer feature) ──────────────────────────────
  async getIssuedCards(): Promise<any> {
    return this.request('/api/corporate/issued-cards');
  }

  async createIssuedCard(data: {
    name: string; title?: string; email?: string; phone?: string;
    mobile?: string; organization?: string; website?: string;
    calendarLink?: string; notes?: string;
  }): Promise<any> {
    return this.request('/api/corporate/issued-cards', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateIssuedCard(id: string, data: {
    name?: string; title?: string; email?: string; phone?: string;
    mobile?: string; organization?: string; website?: string;
    calendarLink?: string; notes?: string;
  }): Promise<any> {
    return this.request(`/api/corporate/issued-cards/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteIssuedCard(id: string): Promise<any> {
    return this.request(`/api/corporate/issued-cards/${id}`, { method: 'DELETE' });
  }

  async toggleIssuedCardLeadForm(id: string, enabled: boolean): Promise<any> {
    return this.request(`/api/corporate/issued-cards/${id}/form-toggle`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    });
  }

  async getIssuedCardLeads(id: string, page = 1): Promise<any> {
    return this.request(`/api/corporate/issued-cards/${id}/leads?page=${page}`);
  }

  async getIssuedCardLeadsExport(id: string, startDate: string, endDate: string): Promise<any> {
    return this.request(`/api/corporate/issued-cards/${id}/leads?limit=2000&startDate=${startDate}&endDate=${endDate}`);
  }

  async getIssuedCardSubscribers(id: string, page = 1): Promise<any> {
    return this.request(`/api/corporate/issued-cards/${id}/subscribers?page=${page}`);
  }

  async getIssuedCardSubscribersExport(id: string, startDate: string, endDate: string): Promise<any> {
    return this.request(`/api/corporate/issued-cards/${id}/subscribers?limit=2000&startDate=${startDate}&endDate=${endDate}`);
  }

  async sendIssuedCardEmail(id: string, payload: {
    to: string;
    subject: string;
    message: string;
    includeCardLink: boolean;
    includeAppleWallet: boolean;
    includeVCard: boolean;
  }): Promise<any> {
    return this.request(`/api/corporate/issued-cards/${id}/send-email`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }
}

// Export singleton instance
export const api = new ApiService(API_BASE_URL);

// Export types
export type { LoginResponse, SignupResponse, ApiResponse };
